import { useEffect, useMemo, useRef } from "react";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const numberFormatter = new Intl.NumberFormat("id-ID", {
  maximumFractionDigits: 1,
});

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function formatDistance(meters) {
  if (meters === null || meters === undefined || !Number.isFinite(Number(meters))) {
    return "—";
  }

  if (Number(meters) < 1000) {
    return `${Math.round(Number(meters))} m`;
  }

  return `${numberFormatter.format(Number(meters) / 1000)} km`;
}

function formatDuration(seconds) {
  if (seconds === null || seconds === undefined || !Number.isFinite(Number(seconds))) {
    return "—";
  }

  const totalMinutes = Math.max(1, Math.round(Number(seconds) / 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (!hours) return `${minutes} menit`;
  if (!minutes) return `${hours} jam`;
  return `${hours} jam ${minutes} menit`;
}

function coordinateStatusLabel(status) {
  const normalized = String(status || "MISSING").toUpperCase();

  if (normalized === "VALID") return "Valid";
  if (normalized === "NEEDS_REVIEW") return "Perlu diperiksa";
  if (normalized === "SUSPECTED_SWAPPED") return "Diduga tertukar";
  if (normalized === "OUT_OF_RANGE") return "Di luar rentang";
  if (normalized === "INCOMPLETE") return "Belum lengkap";
  return "Belum tersedia";
}

function coordinateStatusClass(status) {
  return String(status || "MISSING")
    .toLowerCase()
    .replaceAll("_", "-");
}

export function buildRoutePoints({ depot, stops = [], route }) {
  if (!depot) return [];

  const legs = safeArray(route?.legs);
  const startKey = `depot:${depot.id}:start`;
  const returnKey = `depot:${depot.id}:return`;

  return [
    {
      key: startKey,
      type: "DEPOT_START",
      sequenceLabel: "A",
      title: depot.depotName || "Depot",
      subtitle: "Depot awal",
      address: depot.addressText || "",
      latitude: depot.latitude,
      longitude: depot.longitude,
      coordinateStatus: depot.coordinateStatus,
      leg: null,
    },
    ...stops.map((stop, index) => ({
      key: stop.key,
      type: "STOP",
      sequenceLabel: String(stop.displaySequence || index + 1),
      title: stop.customerName || `Stop ${index + 1}`,
      subtitle: stop.addressAlias || "Alamat default",
      address: [stop.resolvedArea, stop.resolvedRouteName]
        .filter(Boolean)
        .join(" · "),
      latitude: stop.latitude,
      longitude: stop.longitude,
      coordinateStatus: stop.coordinateStatus,
      leg: legs[index] || null,
      salesOrderCount: stop.salesOrderCount || 0,
      itemCount: stop.itemCount || 0,
    })),
    {
      key: returnKey,
      type: "DEPOT_RETURN",
      sequenceLabel: "B",
      title: depot.depotName || "Depot",
      subtitle: "Kembali ke depot",
      address: depot.addressText || "",
      latitude: depot.latitude,
      longitude: depot.longitude,
      coordinateStatus: depot.coordinateStatus,
      leg: legs[stops.length] || null,
    },
  ];
}

export function RoutePointList({
  depot,
  editable = false,
  modeDescription = "Urutan tampilan saat ini",
  modeLabel = "Tersimpan",
  onMoveStop,
  onReorderStops,
  stops = [],
  route,
  selectedKey,
  onSelectPoint,
}) {
  const itemRefs = useRef(new Map());
  const points = useMemo(
    () => buildRoutePoints({ depot, stops, route }),
    [depot, stops, route],
  );
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    if (!selectedKey) return;

    const node = itemRefs.current.get(selectedKey);
    node?.scrollIntoView({
      block: "nearest",
      behavior: "smooth",
    });
  }, [selectedKey]);

  function handleDragEnd(event) {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = stops.findIndex((stop) => stop.key === active.id);
    const newIndex = stops.findIndex((stop) => stop.key === over.id);

    if (oldIndex < 0 || newIndex < 0) return;

    onReorderStops?.(
      arrayMove(stops, oldIndex, newIndex).map((stop, index) => ({
        ...stop,
        displaySequence: index + 1,
      })),
    );
  }

  const startPoint = points[0] || null;
  const returnPoint = points[points.length - 1] || null;

  return (
    <section
      className={`route-point-list ${editable ? "route-point-list--editable" : ""}`.trim()}
      aria-label="Daftar titik rute"
    >
      <header className="route-point-list__header">
        <div>
          <h4>Urutan Rute</h4>
          <small>{modeDescription}</small>
        </div>

        <div className="route-point-list__status">
          <strong>{stops.length} stop</strong>
          <span>{modeLabel}</span>
        </div>
      </header>

      <div className="route-point-list__items">
        {editable && startPoint && (
          <RoutePointItem
            itemRef={itemRefs}
            onSelectPoint={onSelectPoint}
            point={startPoint}
            selected={selectedKey === startPoint.key}
          />
        )}

        {editable ? (
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            sensors={sensors}
          >
            <SortableContext
              items={stops.map((stop) => stop.key)}
              strategy={verticalListSortingStrategy}
            >
              {stops.map((stop, index) => {
                const point = points[index + 1];

                return (
                  <SortableRoutePoint
                    index={index}
                    itemRef={itemRefs}
                    key={stop.key}
                    onMoveStop={onMoveStop}
                    onSelectPoint={onSelectPoint}
                    point={point}
                    selected={selectedKey === point?.key}
                    total={stops.length}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        ) : (
          points.map((point) => (
            <RoutePointItem
              itemRef={itemRefs}
              key={point.key}
              onSelectPoint={onSelectPoint}
              point={point}
              selected={selectedKey === point.key}
            />
          ))
        )}

        {editable && returnPoint && (
          <RoutePointItem
            itemRef={itemRefs}
            onSelectPoint={onSelectPoint}
            point={returnPoint}
            selected={selectedKey === returnPoint.key}
          />
        )}

        {!points.length && (
          <div className="route-point-list__empty">
            Belum ada titik rute.
          </div>
        )}
      </div>
    </section>
  );
}

function RoutePointItem({
  dragHandleProps = null,
  index = 0,
  itemRef,
  onMoveStop,
  onSelectPoint,
  point,
  selected,
  total = 0,
}) {
  if (!point) return null;

  const editable = point.type === "STOP" && onMoveStop;

  function select() {
    onSelectPoint(point);
  }

  return (
    <article
      className={`route-point ${selected ? "is-selected" : ""} ${
        editable ? "route-point--editable" : ""
      }`.trim()}
      onClick={select}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          select();
        }
      }}
      ref={(node) => {
        if (node) {
          itemRef.current.set(point.key, node);
        } else {
          itemRef.current.delete(point.key);
        }
      }}
      role="button"
      tabIndex={0}
    >
      {editable && (
        <button
          aria-label={`Geser ${point.title}`}
          className="route-point__drag-handle"
          onClick={(event) => event.stopPropagation()}
          type="button"
          {...dragHandleProps}
        >
          ⋮⋮
        </button>
      )}

      <span
        className={`route-point__sequence route-point__sequence--${point.type
          .toLowerCase()
          .replaceAll("_", "-")}`}
      >
        {point.sequenceLabel}
      </span>

      <span className="route-point__content">
        <strong>{point.title}</strong>
        <small>{point.subtitle}</small>
        {point.address && <em>{point.address}</em>}

        {point.leg && (
          <span className="route-point__leg">
            Dari titik sebelumnya: {formatDistance(point.leg.distanceM)} ·{" "}
            {formatDuration(point.leg.durationS)}
          </span>
        )}
      </span>

      <span
        className={`coordinate-status coordinate-status--${coordinateStatusClass(
          point.coordinateStatus,
        )}`}
      >
        {coordinateStatusLabel(point.coordinateStatus)}
      </span>

      {editable && (
        <span className="route-point__move-actions">
          <button
            aria-label={`Naikkan ${point.title}`}
            disabled={index === 0}
            onClick={(event) => {
              event.stopPropagation();
              onMoveStop(index, -1);
            }}
            title="Pindahkan ke atas"
            type="button"
          >
            ↑
          </button>
          <button
            aria-label={`Turunkan ${point.title}`}
            disabled={index === total - 1}
            onClick={(event) => {
              event.stopPropagation();
              onMoveStop(index, 1);
            }}
            title="Pindahkan ke bawah"
            type="button"
          >
            ↓
          </button>
        </span>
      )}
    </article>
  );
}

function SortableRoutePoint(props) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props.point?.key,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      className={`route-point-sortable ${isDragging ? "is-dragging" : ""}`.trim()}
      ref={setNodeRef}
      style={style}
    >
      <RoutePointItem
        {...props}
        dragHandleProps={{
          ...attributes,
          ...listeners,
        }}
      />
    </div>
  );
}
